import React, { useRef, useState, useEffect } from "react";
import i18n from "i18next";

import UserMedia from "./UserMedia";
import TechnicalData from "./TechnicalData";
import FreigabeDialog from "../FreigabeDialog/FreigabeDialog";

// Internal imports
import { fetchWrapper } from "../../helpers/fetchwrapper";
import { baseUrl } from "../../helpers/constants";
import BOMCommandBar from "./BOMCommandBar";
import BOMList from "./BOMList";
import AddAllWearingPartsDialog from "./AddAllWearingPartsDialog";
import ProductDocumentsCommandbar from "./ProductDocumentsCommandBar";
import ProductDocuments from "./ProductDocuments";
import UserProductCommandbar from "./UserProductCommandBar";

// Internal Models Imports
import { IGetriebe } from "../../Models/IGetriebe";
import { IProduktDto } from "../../Models/IProdukt";
import { IStuelipos } from "../../Models/IStuelipos";
import { IStueli } from "../../Models/IStueli";
import { IUserProduct } from "../../Models/IUserProduct";

// Fluent UI Imports
import {
  MessageBar,
  MessageBarBody,
  Overflow,
  OverflowItem,
  ProgressBar,
  Tab,
  TabList,
  Text,
} from "@fluentui/react-components";
import { StackShim } from "@fluentui/react-migration-v8-v9";
import {
  AppsListDetailRegular,
  CommentMultipleRegular,
  DocumentFolderRegular,
  ImageStackRegular,
  TextBulletListTreeRegular,
} from "@fluentui/react-icons";
import { IProductDocuments } from "../../Models/UserProductDocuments";
import { IUserMedia } from "../../Models/IUserMedia";
import UserMediaCommandbar from "./UserMediaCommandBar";
import OverflowMenu from "../../Controls/OverflowMenu";
import Comments from "../Base/Comments";
import { IComment } from "../../Models/IComment";

interface IProductDetailsProps {
  product: IProduktDto;
  userProduct?: IUserProduct;
  isPanelOpen: boolean;
  salesView: boolean;
  onDismiss(): void;
  onRefresh(): void;
}

function ProductDetails(props: IProductDetailsProps) {
  const [comments, setComments] = useState<IComment[]>();
  const getComments = (guid: string) => {
    fetchWrapper
      .get(`${baseUrl}/api/comment/GetComments?refGuid1=${guid}`)
      .then((comments: IComment[]) => {
        setComments(comments);
      })
      .catch((error: any) => {});
  };
  const [loadingDocs, setLoadingDocs] = useState<boolean>(false);
  const [selectedTab, setSelectedTab] = useState<string>("general");
  const keys = [
    {
      key: "general",
      text: i18n.t<string>("general"),
      icon: <AppsListDetailRegular />,
    },

    {
      key: "documentsDrawings",
      text: i18n.t<string>("documentsDrawings"),
      icon: <DocumentFolderRegular />,
    },
  ];

  if (
    props.product?.artikel &&
    !props.product?.artikel?.artikelId?.includes("VSBT") &&
    !props.product?.artikel?.benennungDE?.includes("L.ADU")
  ) {
    keys.splice(1, 0, {
      key: "compBoms",
      text: i18n.t<string>("compBoms"),
      icon: <TextBulletListTreeRegular />,
    });
  }

  if (props.userProduct) {
    var commentCountString = comments ? comments.length.toLocaleString() : "0";
    keys.push(
      {
        key: "userMedia",
        text: i18n.t<string>("userMedia"),
        icon: <ImageStackRegular />,
      },
      {
        key: "comments",
        text: i18n.t<string>("comments") + " ( " + commentCountString + " )",
        icon: <CommentMultipleRegular />,
      }
    );
  }

  const [freigabeObjectGuid, setFreigabeObjectGuid] = useState(undefined);

  const emptyList: IStuelipos[] = [];

  const [addAllWearingParts, setAddAllWearingParts] = useState<any[]>([]);

  const [showOnlyWearingParts, setShowOnlyWearingParts] =
    useState<boolean>(false);

  const [bom, setBom] = useState<IStuelipos[]>([]);
  const [displayBom, setDisplayBom] = useState<IStuelipos[]>(emptyList);

  const [dokumente, setDokumente] = useState<IProductDocuments | undefined>(
    undefined
  );
  const [userMedia, setUserMedia] = useState<IUserMedia | undefined>(undefined);

  const [productDetails, setProductDetails] = useState<IGetriebe>({
    maxAntriebsmoment: 0.0,
    maxAbtriebsmoment: 0.0,
    wirkungsgrad: 0.0,
    uebersetzung: 0.0,
    antriebsmoment: 0.0,
    abtriebsmoment: 0.0,
    momenteneinheit: "",
    productPositions: [],
  });

  const expandAll = () => {
    bom.forEach((bomItem: any) => {
      if (bomItem.hasStueli) {
        bomItem.expanded = true;
      }
    });
    setBom(bom.map((x) => x));
  };

  const collapseAll = () => {
    bom.forEach((bomItem: any) => {
      if (bomItem.hasStueli) {
        bomItem.expanded = false;
      }
    });
    setBom(bom.map((x) => x));
  };

  // Liefert true (wenn alle Bäume aufgeklappt sind) oder false (wenn mindestens eine Position zugeklappt ist)
  const areAllExpanded = !displayBom.some(
    (bomItem: any) => bomItem.hasStueli && !bomItem.expanded
  );

  const addAllwearingPartsToBedarf = () => {
    const itemsToAdd: any[] = [];
    bom.forEach((item: any) => {
      if (item.isVerschleissteil) {
        itemsToAdd.push({
          artikel: item.artikelId,
          produktId: undefined,
          menge: item.menge,
          kundenReferenz: props.product.produktId + " " + benennung,
        });
      }
    });
    setAddAllWearingParts(itemsToAdd);
  };

  const expandBom = (item: IStuelipos) => {
    bom.forEach((bomItem: IStuelipos) => {
      if (
        bomItem.artikelId == item.artikelId &&
        bomItem.position == item.position
      ) {
        bomItem.expanded = !bomItem.expanded;
      }
    });
    setBom(bom.map((x) => x));
  };

  useEffect(() => {
    setBom(bom.map((x) => x));
  }, [showOnlyWearingParts]);

  const hasOrIsVerschleissteile = (stuelipos: any) => {
    // Prüft ob die Position ein Verschleißteil ist oder unterordnet Verschleißteile vorhanden sind
    if (stuelipos.isVerschleissteil) {
      return true;
    }

    let subIsOrHasVerschleissteil = false;

    const subItems = bom.filter(
      (subItem: IStuelipos) => subItem.stueliId === stuelipos.artikelId
    );

    subItems.forEach((subPos: IStuelipos) => {
      if (subPos.isVerschleissteil) {
        subIsOrHasVerschleissteil = true;
      } else if (hasOrIsVerschleissteile(subPos)) {
        subIsOrHasVerschleissteil = true;
      }
    });
    return subIsOrHasVerschleissteil;
  };

  const addItemToStueli = (
    sortedBom: IStuelipos[],
    displayBom: IStuelipos[],
    item: IStuelipos
  ) => {
    if (item.hasStueli && item.expanded) {
      // Unterpositionen finden und hinzufügen
      const list = sortedBom.filter(
        (subItem: IStuelipos) => subItem.stueliId == item.artikelId
      );
      for (let i = 0; i < list.length; i++) {
        if (item.ebene != undefined) {
          // Ebene für die Einfärbung setzen
          (list[i] as IStuelipos).ebene = item.ebene + 1;
          if (
            !showOnlyWearingParts ||
            (showOnlyWearingParts &&
              hasOrIsVerschleissteile(list[i] as IStuelipos))
          ) {
            displayBom.push(list[i]);
            displayBom = addItemToStueli(sortedBom, displayBom, list[i]);
          }
        }
      }
    }
    return displayBom;
  };

  useEffect(() => {
    let displayBom: IStuelipos[] = [];
    const sortedBom = bom;
    for (let i = 0; i < sortedBom.length; i++) {
      const item = sortedBom[i] as IStuelipos;
      if ((!item.stueliId || item.stueliId == "") && !showOnlyWearingParts) {
        // Dies ist das oberste Item, hinzufügen wenn alles angezeigt werden soll.
        item.ebene = 0;
        displayBom.push(item);
        displayBom = addItemToStueli(sortedBom, displayBom, item);
      }

      const hasOrIsVerschleissteil = hasOrIsVerschleissteile(item);

      if (
        (!item.stueliId || item.stueliId == "") &&
        showOnlyWearingParts &&
        hasOrIsVerschleissteil
      ) {
        // Wenn nur Verschleißteile angezeigt werden sollen, prüfen ob untergeordnet Verschleißteile vorhanden sind
        item.ebene = 0;
        displayBom.push(item);
        displayBom = addItemToStueli(sortedBom, displayBom, item);
      }
    }
    setDisplayBom(displayBom);
  }, [bom]);

  const getDocuments = () => {
    // Dokumente holen
    if (!props.product || !props.product.produktId) {
      return;
    }
    setLoadingDocs(true);
    fetchWrapper
      .getWithAuth(
        `${baseUrl}/api/documents/ProduktDokumentePartial?produktId=${props.product.produktId}&userProductGuid=${props.userProduct?.userProduktGuid}`
      )
      .then((data: IProductDocuments) => {
        if (!data) {
          return;
        }
        setDokumente(data);
      })
      .catch((error) => {})
      .finally(() => {
        setLoadingDocs(false);
      });
  };

  const getUserMedia = () => {
    if (props.userProduct && props.userProduct.userProduktGuid) {
      fetchWrapper
        .getWithAuth(
          `${baseUrl}/api/UserMedia/GetUserMediaForProduct?id=${props.userProduct.userProduktGuid}`
        )
        .then((result) => {
          setUserMedia(result);
        });
    }
  };

  const getProductDetails = () => {
    if (props.product && props.product.produktId) {
      // Produktdetails holen
      fetchWrapper
        .getWithAuth(
          `${baseUrl}/api/product/Details?id=${
            props.product.produktId
          }&removeDocs=${props.userProduct?.userProduktGuid != undefined}`
        )
        .then(async (data) => {
          if (
            data.produkt?.artikel?.artikelId // && !data.produkt.artikel.artikelId.includes("VSBT")
          ) {
            var i = 0;
            const bomList: IStuelipos[] = [];
            bomList.splice(0);
            data.productPositions.forEach(async (pos: IStuelipos) => {
              i = i + 1;
              const bomItem: IStuelipos = {
                artikelId: pos.artikelId,
                artikel: pos.artikel,
                hasStueli: pos.hasStueli,
                expanded: false,
                stueliId: "",
                benennungDE: pos.benennungDE,
                benennungEN: pos.benennungEN,
                menge: pos.menge,
                position: pos.position,
                seriennummer: pos.seriennummer,
                ebene: 0,
                canAddToBedarf: pos.canAddToBedarf,
                hasDocuments: pos.hasDocuments,
                hasSachwerte: pos.hasSachwerte,
              };
              bomList.push(bomItem);
              if (pos.hasStueli) {
                // BOM holen
                await fetchWrapper
                  .getWithAuth(
                    `${baseUrl}/api/bom/getbom?productId=${props.product.produktId}&artikelId=${pos.artikelId}`
                  )
                  .then((data: IStueli) => {
                    data.positionen.forEach((element: IStuelipos) => {
                      const bomItem: IStuelipos = {
                        artikelId: element.artikelId,
                        artikel: element.artikel,
                        hasStueli: element.hasStueli,
                        hasSachwerte: element.hasSachwerte,
                        hasDocuments: element.hasDocuments,
                        expanded: false,
                        stueliId: element.stueliId,
                        isVerschleissteil:
                          element.isVerschleissteil ||
                          element.artikel?.isVerschleissteil,
                        benennungDE: element.artikel?.benennungDE,
                        benennungEN: element.artikel?.benennungEN,
                        menge: element.menge,
                        position: Number(element.positionNr),
                        ebene: undefined,
                        canAddToBedarf: element.canAddToBedarf,
                      };
                      bomList.push(bomItem);
                    });
                  })
                  .catch((error) => {});
              }

              if (i == data.productPositions.length) {
                setProductDetails(data);
                setBom(
                  bomList
                    .sort((a: IStuelipos, b: IStuelipos) =>
                      a.position && b.position && a.position < b.position
                        ? -1
                        : 1
                    )
                    .map((x) => x)
                );
              }
            });
          } else {
            setProductDetails(data);
          }
        })
        .catch((error) => {});
    }
  };

  useEffect(() => {
    if (selectedTab == "documentsDrawings") {
      getDocuments();
    }
  }, [selectedTab]);

  useEffect(() => {
    if (selectedTab == "userMedia") {
      getUserMedia();
    }
  }, [selectedTab]);

  useEffect(() => {
    getProductDetails();
  }, [props.product]);

  useEffect(() => {
    if (props.userProduct?.userProduktGuid) {
      getComments(props.userProduct?.userProduktGuid);
    }
  }, [props.userProduct?.userProduktGuid]);

  let benennung: string | undefined;
  let produktId = "";

  if (props.product?.artikel) {
    produktId = props.product?.produktId;
    if (i18n.language.includes("de")) {
      benennung = props.product?.artikel?.benennungDE;
    } else {
      benennung = props.product?.artikel?.benennungEN;
    }
  }

  return (
    <div>
      {addAllWearingParts.length > 0 && (
        <AddAllWearingPartsDialog
          items={addAllWearingParts}
          onDismiss={() => setAddAllWearingParts([])}
        />
      )}

      {freigabeObjectGuid != undefined && (
        <FreigabeDialog
          onDismiss={() => {
            setFreigabeObjectGuid(undefined);
          }}
          onSave={() => {
            props.onRefresh();
            setFreigabeObjectGuid(undefined);
          }}
          isDialogOpen={freigabeObjectGuid != undefined}
          objectGuid={freigabeObjectGuid ? freigabeObjectGuid! : ""}
          objectType="UserDocument"
          referenceGuid=""
        />
      )}

      <div>
        {selectedTab == "general" && props.userProduct && (
          <UserProductCommandbar
            userProduct={props.userProduct}
            onRefresh={() => {
              props.onRefresh();
            }}
          />
        )}

        {selectedTab == "compBoms" && (
          <BOMCommandBar
            isBomExpanded={areAllExpanded}
            onBomExpandedChanged={(isExpanded) => {
              isExpanded ? expandAll() : collapseAll();
            }}
            showOnlyWearingParts={showOnlyWearingParts}
            onShowOnlyWearingPartsChanged={(checked) => {
              setShowOnlyWearingParts(checked ? checked : false);
            }}
            onAddWearingParts={() => addAllwearingPartsToBedarf()}
          />
        )}

        {selectedTab == "documentsDrawings" && (
          <ProductDocumentsCommandbar
            userProduct={props.userProduct}
            onDocumentUploaded={() => {
              getDocuments();
            }}
          />
        )}

        {selectedTab == "userMedia" && (
          <UserMediaCommandbar
            userProduct={props.userProduct}
            onImageUploaded={() => {
              getUserMedia();
            }}
          />
        )}

        <Overflow minimumVisible={2}>
          <TabList
            selectedValue={selectedTab}
            onTabSelect={(ev, data: any) => {
              if (data && data.value) {
                setSelectedTab(data.value);
              }
            }}
          >
            {keys.map((item) => {
              if (item.key != "compBoms") {
                return (
                  <OverflowItem
                    key={item.key}
                    id={item.key}
                    priority={item.key === selectedTab ? 2 : 1}
                  >
                    <Tab icon={item.icon} value={item.key}>
                      <StackShim
                        horizontal
                        verticalAlign="center"
                        tokens={{ childrenGap: 5 }}
                      >
                        <Text>{item.text}</Text>
                      </StackShim>
                    </Tab>
                  </OverflowItem>
                );
              } else {
                return (
                  <>
                    {props.product?.artikel && (
                      <OverflowItem
                        key={item.key}
                        id={item.key}
                        priority={item.key === selectedTab ? 2 : 1}
                      >
                        <Tab
                          icon={<TextBulletListTreeRegular />}
                          value={item.key}
                        >
                          <StackShim
                            horizontal
                            verticalAlign="center"
                            tokens={{ childrenGap: 5 }}
                          >
                            <Text>{item.text}</Text>
                          </StackShim>
                        </Tab>
                      </OverflowItem>
                    )}
                  </>
                );
              }
            })}
            {
              <OverflowMenu
                onTabSelect={(tabId: string) => {
                  setSelectedTab(tabId);
                }}
                items={keys.filter((x) => x.key != selectedTab)}
              />
            }
          </TabList>
        </Overflow>

        {selectedTab == "general" && (
          <TechnicalData
            userProduct={props.userProduct}
            product={props.product}
            productDetails={productDetails}
            onReload={() => props.onRefresh()}
          />
        )}

        {selectedTab == "compBoms" && (
          <BOMList
            bom={displayBom}
            expandBom={(bomItem) => {
              expandBom(bomItem);
            }}
          />
        )}

        {selectedTab == "documentsDrawings" && (
          <>
            {loadingDocs ? (
              <ProgressBar style={{ marginTop: "20px" }} />
            ) : (
              <ProductDocuments
                dokumente={dokumente}
                product={props.product}
                userProduct={props.userProduct}
                onRefresh={() => {
                  getDocuments();
                }}
              />
            )}
          </>
        )}

        {selectedTab == "userMedia" &&
          !props.salesView &&
          props.userProduct?.userProduktGuid && (
            <UserMedia
              userMedia={userMedia}
              userProduktGuid={props.userProduct.userProduktGuid}
              onRefresh={() => {
                getUserMedia();
              }}
            />
          )}

        {selectedTab == "comments" && props.userProduct && (
          <Comments
            comments={comments ?? []}
            context={"UserProducts"}
            refGuid1={props.userProduct.userProduktGuid}
            objectTitle={
              props.userProduct.userProduktGuid +
              " - " +
              i18n.language.includes("de")
                ? props.userProduct.benennungDE
                : props.userProduct.benennungEN
            }
            onCommentAdded={() => {
              if (props.userProduct?.userProduktGuid)
                getComments(props.userProduct?.userProduktGuid);
            }}
          />
        )}
      </div>
    </div>
  );
}

export default ProductDetails;
